import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Site-wide password gate for the closed-beta window. Mounts at the very top
 * of the tree (above the router + AuthProvider) so that:
 *
 *  1. On first render we hit `/api/site-access/status` — this is the ONLY
 *     unauthenticated endpoint the backend accepts without the site-access
 *     cookie. It tells us whether the gate is on (`enabled`) and whether the
 *     current visitor is already inside (`unlocked`).
 *  2. If the gate is off (empty env) or the visitor is already unlocked, we
 *     render `children` and get out of the way.
 *  3. Otherwise we show a single-input password screen. Submit posts to
 *     `/api/site-access/verify`; on 200 we set local state and re-render.
 *
 * Cookies are session-only (dies on browser close), so testers re-enter the
 * password once per browsing session.
 */
export default function SiteGate({ children }) {
  const [state, setState] = useState({ ready: false, enabled: false, unlocked: true });
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/site-access/status")
      .then((r) => setState({ ready: true, enabled: !!r.data.enabled, unlocked: !!r.data.unlocked }))
      .catch(() => setState({ ready: true, enabled: false, unlocked: true }));
  }, []);

  if (!state.ready) return null;
  if (!state.enabled || state.unlocked) return children;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await http.post("/site-access/verify", { password });
      setState((s) => ({ ...s, unlocked: true }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Incorrect password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F7F5F2", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 420, width: "100%", background: "#fff", padding: "3rem 2.5rem", border: "1px solid #E5E1D8" }} data-testid="site-gate">
        <div style={{ fontFamily: "'GT Sectra', 'Playfair Display', Georgia, serif", fontSize: "2rem", lineHeight: 1.1, marginBottom: "0.25rem", letterSpacing: "-0.01em" }}>BrandPassport</div>
        <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.3em", color: "#6b6b6b", marginBottom: "1.75rem" }}>Closed beta</div>
        <p style={{ fontSize: "0.95rem", color: "#3a3a3a", marginBottom: "1.5rem", lineHeight: 1.5 }}>
          This site is in restricted preview. Enter the access password provided by the team to continue.
        </p>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <Input
            type="password"
            autoFocus
            required
            placeholder="Access password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="site-gate-password"
            style={{ borderRadius: 0 }}
          />
          {error && <div data-testid="site-gate-error" style={{ fontSize: "0.85rem", color: "#B84A3F" }}>{error}</div>}
          <Button type="submit" disabled={busy || !password} data-testid="site-gate-submit" className="rounded-none" style={{ width: "100%" }}>
            {busy ? "Verifying…" : "Enter"}
          </Button>
        </form>
        <p style={{ fontSize: "0.75rem", color: "#8a8a8a", marginTop: "1.5rem", lineHeight: 1.5 }}>
          The password gives you access for this browser session. Editors and brand reps will still need to sign in with their own credentials on top of this.
        </p>
      </div>
    </div>
  );
}
